import React from 'react';
import { Card, Empty, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { BannerRow, OfferRow } from '@/types';
import { fmtNum, fmtPct } from '../bq-insights/bqMath';
import { inr, rate, slotLabel, sumOf } from './monetizationMath';
import type { TypedEntry } from './monetizationTabState';
import { titleWithInfo, totalRow } from './tableHelpers';
import { TabBody } from './TabBody';

const { Text } = Typography;

const bannerColumns: ColumnsType<BannerRow> = [
  { title: 'Slot', dataIndex: 'slot', render: (s: string) => slotLabel(s) },
  { title: 'Shown', dataIndex: 'shown', align: 'right', render: (v) => fmtNum(v) },
  { title: 'Closed', dataIndex: 'closed', align: 'right', render: (v) => fmtNum(v) },
  { title: 'Clicked', dataIndex: 'clicked', align: 'right', render: (v) => fmtNum(v) },
  { title: 'IAP success', dataIndex: 'iapSuccess', align: 'right', render: (v) => fmtNum(v) },
  {
    title: 'IAP cancelled',
    dataIndex: 'iapCancelled',
    align: 'right',
    render: (v) => fmtNum(v),
  },
  { title: 'IAP failed', dataIndex: 'iapFailed', align: 'right', render: (v) => fmtNum(v) },
  {
    title: titleWithInfo('Close rate', 'Closed / shown'),
    align: 'right',
    render: (_, r) => fmtPct(rate(r.closed, r.shown)),
  },
  {
    title: titleWithInfo('Click rate', 'Clicked / shown'),
    align: 'right',
    render: (_, r) => fmtPct(rate(r.clicked, r.shown)),
  },
  {
    title: titleWithInfo('Click to purchase', 'IAP success / clicked'),
    align: 'right',
    render: (_, r) => fmtPct(rate(r.iapSuccess, r.clicked)),
  },
  {
    title: titleWithInfo('Shown to purchase', 'IAP success / shown'),
    align: 'right',
    render: (_, r) => fmtPct(rate(r.iapSuccess, r.shown)),
  },
];

const offerColumns: ColumnsType<OfferRow> = [
  { title: 'Offer', dataIndex: 'offerId' },
  { title: 'Trigger', dataIndex: 'trigger' },
  { title: 'Variant', dataIndex: 'variant' },
  { title: 'Shown', dataIndex: 'shown', align: 'right', render: (v) => fmtNum(v) },
  { title: 'Clicked', dataIndex: 'clicked', align: 'right', render: (v) => fmtNum(v) },
  {
    title: 'Purchase started',
    dataIndex: 'purchaseStarted',
    align: 'right',
    render: (v) => fmtNum(v),
  },
  { title: 'Purchased', dataIndex: 'purchased', align: 'right', render: (v) => fmtNum(v) },
  {
    title: 'Purchased (backend)',
    dataIndex: 'backendPurchased',
    align: 'right',
    render: (v) => fmtNum(v),
  },
  {
    title: 'Revenue (backend, INR)',
    dataIndex: 'backendRevenueInr',
    align: 'right',
    render: (v) => inr(v),
  },
  {
    title: titleWithInfo('Click rate', 'Clicked / shown'),
    align: 'right',
    render: (_, r) => fmtPct(rate(r.clicked, r.shown)),
  },
  {
    title: titleWithInfo('Start rate', 'Purchase started / clicked'),
    align: 'right',
    render: (_, r) => fmtPct(rate(r.purchaseStarted, r.clicked)),
  },
  {
    title: titleWithInfo('Completion', 'Purchased / purchase started'),
    align: 'right',
    render: (_, r) => fmtPct(rate(r.purchased, r.purchaseStarted)),
  },
  {
    title: titleWithInfo('Shown to purchase', 'Purchased / shown'),
    align: 'right',
    render: (_, r) => fmtPct(rate(r.purchased, r.shown)),
  },
];

const BannerTable: React.FC<{ rows: BannerRow[] }> = ({ rows }) => {
  if (rows.length === 0) {
    return (
      <Empty
        description="No home popup events in this range"
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }
  const s = (pick: (r: BannerRow) => number | null) => sumOf(rows, pick);
  return (
    <Table<BannerRow>
      size="small"
      rowKey="slot"
      columns={bannerColumns}
      dataSource={rows}
      pagination={false}
      scroll={{ x: 'max-content' }}
      summary={() =>
        totalRow([
          null,
          fmtNum(s((r) => r.shown)),
          fmtNum(s((r) => r.closed)),
          fmtNum(s((r) => r.clicked)),
          fmtNum(s((r) => r.iapSuccess)),
          fmtNum(s((r) => r.iapCancelled)),
          fmtNum(s((r) => r.iapFailed)),
          fmtPct(
            rate(
              s((r) => r.closed),
              s((r) => r.shown)
            )
          ),
          fmtPct(
            rate(
              s((r) => r.clicked),
              s((r) => r.shown)
            )
          ),
          fmtPct(
            rate(
              s((r) => r.iapSuccess),
              s((r) => r.clicked)
            )
          ),
          fmtPct(
            rate(
              s((r) => r.iapSuccess),
              s((r) => r.shown)
            )
          ),
        ])
      }
    />
  );
};

const OfferTable: React.FC<{ rows: OfferRow[] }> = ({ rows }) => {
  if (rows.length === 0) {
    return (
      <Empty
        description="No offer card events in this range (they start with 0.5 builds)"
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }
  const s = (pick: (r: OfferRow) => number | null) => sumOf(rows, pick);
  return (
    <Table<OfferRow>
      size="small"
      rowKey={(r) => `${r.offerId}|${r.trigger}|${r.variant}`}
      columns={offerColumns}
      dataSource={rows}
      pagination={false}
      scroll={{ x: 'max-content' }}
      summary={() =>
        totalRow([
          null,
          null,
          null,
          fmtNum(s((r) => r.shown)),
          fmtNum(s((r) => r.clicked)),
          fmtNum(s((r) => r.purchaseStarted)),
          fmtNum(s((r) => r.purchased)),
          fmtNum(s((r) => r.backendPurchased)),
          inr(s((r) => r.backendRevenueInr)),
          fmtPct(
            rate(
              s((r) => r.clicked),
              s((r) => r.shown)
            )
          ),
          fmtPct(
            rate(
              s((r) => r.purchaseStarted),
              s((r) => r.clicked)
            )
          ),
          fmtPct(
            rate(
              s((r) => r.purchased),
              s((r) => r.purchaseStarted)
            )
          ),
          fmtPct(
            rate(
              s((r) => r.purchased),
              s((r) => r.shown)
            )
          ),
        ])
      }
    />
  );
};

interface Props {
  banners: TypedEntry<BannerRow[]>;
  offers: TypedEntry<OfferRow[]>;
  onRetry?: () => void;
}

/** The home popup funnel and the offer card funnel, each with its own state. */
export const FunnelsTab: React.FC<Props> = ({ banners, offers, onRetry }) => (
  <div>
    <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 8 }}>
      Counts are events over the whole range; rates divide one column by another. No player counts
      here: players are not additive across days.
    </Text>
    <div data-section="banners">
      <Card size="small" title="Home popup (banner)" style={{ marginBottom: 12 }}>
        <TabBody entry={banners} onRetry={onRetry}>
          {(rows) => <BannerTable rows={rows} />}
        </TabBody>
      </Card>
    </div>
    <div data-section="offers">
      <Card size="small" title="Offer cards">
        <TabBody entry={offers} onRetry={onRetry}>
          {(rows) => <OfferTable rows={rows} />}
        </TabBody>
      </Card>
    </div>
  </div>
);
